import { CommonModule } from '@angular/common';
import {
  Component,
  ComponentRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { QaiButtonComponent, QaiFieldComponent, QaiTextareaComponent, QaiTrimLeadingDirective, TextareaAutosizeDirective, QaiPasteCleanDirective, QaiIconComponent, QaiLabelComponent, DraggableItem, DraggableContainerComponent } from '@qai/ui';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { MessagingThemeComponent } from './messaging-theme/messaging-theme.component';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs/operators';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { ActivatedRoute } from '@angular/router';
import { ISimulationPrompt } from 'src/app/interfaces/self-serve/isimulation-prompt';
import { ISectionsWithFacts } from 'src/app/interfaces/self-serve/requests/ISectionsWithFacts-update';
import { ISection } from 'src/app/interfaces/self-serve/responses/ISectionsWithFacts';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';

@Component({
  selector: 'messaging-section',
  standalone: true,
  templateUrl: './messaging-section.component.html',
  styleUrls: ['./messaging-section.component.scss'],
  imports: [
    QaiFieldComponent,
    FormsModule,
    CommonModule,
    QaiTextareaComponent,
    TextareaAutosizeDirective,
    QaiTrimLeadingDirective,
    DetailsSectionComponent,
    QaiButtonComponent,
    ReactiveFormsModule,
    QaiPasteCleanDirective,
    QaiIconComponent,
    QaiLabelComponent,
    DraggableContainerComponent,
    MessagingThemeComponent
],
})
export class MessagingSectionComponent {
  form!: FormGroup;
  messagingPrompt!: FormGroup;

  callUuid: string = '';
  @ViewChild('themesContainer', { read: ViewContainerRef, static: true })
  container!: ViewContainerRef;

  // Store component references by section UUID
  private componentRefs = new Map<
    string,
    ComponentRef<MessagingThemeComponent>
  >();

  messagingThemeGroup!: FormGroup;
  messagingFactSections!: FormArray;

  themesItems: DraggableItem[] = [];

  readOnly: boolean = false;

  messagingPromptLabel: string = 'Scenario prompt';
  defaultCorrectMessage: string = '';
  defaultIncorrectMessage: string = '';
  detailsSectionDescription: string =
    'Define the messages the AI will evaluate during a simulation call.';
  themesLabel: string = 'Themes';

  private shouldStartEditing = false;

  constructor(
    private authorService: AuthorService,
    private authorReactiveFormService: AuthorReactiveFormService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
  ) {}

  generate6CharString = (): string => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap?.get('uuid')!;
    this.loadFormAndSetupAutoSave();

    this.authorService
      .getSectionsWithFacts(this.callUuid)
      .subscribe((response) => {
        console.log('Response: ', response);

        this.replaceMessagingThemeFactSections({ factSections: response });
      });

    this.setReadOnlyMode();
  }

  private setReadOnlyMode() {
    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);
    this.authorReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.messagingPromptGroup?.get('promptText'),
    );
  }

  private updateMessageAdherencePromptTypeInForm(newPromptType: string): void {
    if (!this.messagingPrompt) {
      console.warn('messagingPrompt FormGroup is not initialized');
      return;
    }

    const prompt = this.messagingPrompt.getRawValue();

    prompt.promptType = newPromptType;
    this.messagingPrompt.patchValue({ promptType: prompt.promptType });

    // Update section weights to trigger the existing subscription in overall-section
    this.authorReactiveFormService.updateSectionWeightsForMessagingPromptType(
      this.callUuid,
      newPromptType,
    );

    if (!prompt.uuid) {
      console.warn('No UUID — form patched, skipping API call');
      return;
    }

    this.authorService
      .updateSimulationPrompts(this.callUuid, [prompt])
      .subscribe({
        next: (res) => {
          console.log('Update simulation prompts response:', res);
        },
        error: (err) => {
          console.error('Error updating simulation prompts:', err);
        },
      });
  }

  replaceMessagingThemeFactSections(newSections: ISectionsWithFacts): void {
    this.messagingThemeGroup = this.form?.get(
      SimulationConstants.MESSAGING_THEME_SECTION,
    ) as FormGroup;

    if (!this.messagingThemeGroup) return;

    const factSectionsArray = new FormArray<FormGroup>([]); // Build a *new* FormArray

    newSections.factSections.forEach((section) => {
      const factsFormArray = this.fb.array(
        section.facts.map((fact) =>
          this.fb.group({
            uuid: [fact.uuid || ''],
            rawText: [fact.rawText || ''],
            displayTextCorrect: [
              fact.displayTextCorrect || this.defaultCorrectMessage,
            ],
            displayTextIncorrect: [
              fact.displayTextIncorrect || this.defaultIncorrectMessage,
            ],
            threshold: [fact.threshold ?? 0],
          }),
        ),
      );

      const sectionGroup = this.fb.group({
        uuid: [section.uuid || ''],
        name: [section.name || ''],
        facts: factsFormArray,
      });

      factSectionsArray.push(sectionGroup);
    });

    // Replace the entire 'factSections' FormArray
    this.messagingThemeGroup.setControl(
      SimulationConstants.FACT_SECTIONS_SECTION,
      factSectionsArray,
    );
    console.log('messagingThemeGroup', this.messagingThemeGroup);

    this.messagingFactSections = this.messagingThemeGroup?.get(
      SimulationConstants.FACT_SECTIONS_SECTION,
    ) as FormArray;
    this.syncThemesItems();
  }

  addTheme(_event: Event) {
    const defaultName = this.generateDefaultThemeName();
    this.shouldStartEditing = true;

    this.triggerSectionSave(this.callUuid, {
      factSections: [
        {
          name: defaultName,
          facts: [
            {
              rawText: '',
              displayTextCorrect: this.defaultCorrectMessage,
              displayTextIncorrect: this.defaultIncorrectMessage,
              threshold: 7.0,
            },
          ],
        },
      ],
    });
  }

  private generateDefaultThemeName(): string {
    const baseName = 'New Theme';
    const existingNames: string[] = (
      this.messagingFactSections?.value || []
    ).map((s: any) => s.name);
    if (!existingNames.includes(baseName)) return baseName;
    let i = 2;
    while (existingNames.includes(`${baseName} ${i}`)) i++;
    return `${baseName} ${i}`;
  }

  get messagingPromptGroup(): FormGroup {
    return this.form?.get(
      SimulationConstants.MESSAGING_PROMPT_SECTION,
    ) as FormGroup;
  }

  private loadFormAndSetupAutoSave(): void {
    this.authorReactiveFormService?.getForm(this.callUuid).subscribe((form) => {
      this.form = form?.get(SimulationConstants.SCORING_SECTION) as FormGroup;
      this.messagingPrompt = form?.get(
        SimulationConstants.MESSAGING_PROMPT_PATH,
      ) as FormGroup;
      this.setupAutoSave(
        this.messagingPrompt,
        SimulationConstants.MESSAGING_PROMPT_PATH,
      );
    });
  }

  private setupAutoSave(section: FormGroup, formPath: string): void {
    const promptTextControl = section?.get('promptText');

    if (!promptTextControl) return;

    let previousValue: string = promptTextControl.getRawValue();

    promptTextControl.valueChanges
      .pipe(
        skip(1), // ignore initial form init change
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe((newVal) => {
        const oldValue = previousValue;
        previousValue = newVal;

        const updatedValue = section.getRawValue();

        const trimmedOld = (oldValue || '').trim();
        const trimmedNew = (newVal || '').trim();

        if (trimmedOld === trimmedNew && trimmedNew === '') {
          return;
        }

        this.authorReactiveFormService.patchForm(
          this.callUuid,
          formPath,
          updatedValue,
        );

        const readOnly = this.authorReactiveFormService.isReadOnly(
          this.callUuid,
        );

        if (updatedValue && !readOnly) {
          this.triggerPromptSave(this.callUuid, updatedValue, formPath);
        }
      });
  }

  triggerPromptSave(
    callUuid: string,
    prompt: ISimulationPrompt,
    formPath: string,
  ): void {
    const payload = { prompts: [prompt] };

    let request$;

    if (prompt.uuid) {
      request$ = this.authorService.updateSimulationPrompts(
        callUuid,
        payload.prompts,
      );
    } else {
      if (this.messagingFactSections.length > 0) {
        prompt.promptType = SimulationConstants.MESSAGING_PROMPT_TYPE_V2;
      }
      request$ = this.authorService.createSimulationPrompts(
        callUuid,
        payload.prompts,
      );
    }

    request$.subscribe((response) => {
      const updatedPrompt = response?.prompts?.[0];
      if (!updatedPrompt?.uuid) return;

      this.authorReactiveFormService.patchForm(callUuid, formPath, {
        uuid: updatedPrompt.promptText === '' ? null : updatedPrompt.uuid,
        promptType: prompt.promptType,
      });

      this.form?.updateValueAndValidity();
    });
  }

  private triggerSectionSave(
    callUuid: string,
    sections: ISectionsWithFacts,
  ): void {
    const request$ = sections.factSections[0].uuid
      ? this.authorService.updateSectionWithFacts(callUuid, sections)
      : this.authorService.createSectionWithFacts(callUuid, sections);

    request$.subscribe((response) => {
      const updatedSection = response?.factSections?.[0];
      if (!updatedSection?.uuid) return;

      this.updateLastMessagingThemeUuid(updatedSection);
    });
  }

  updateLastMessagingThemeUuid(updatedSection: ISection): void {
    const lastIndex = this.messagingFactSections.length - 1;
    let lastFactsSection = this.messagingFactSections.at(
      lastIndex,
    ) as FormGroup;

    let newFactSection;
    let newFactSectionsFacts;

    /* As first section is being injected at the time of reactive form initialization,
    we section instance is there, need to update its values. */
    if (lastIndex === 0 && !lastFactsSection?.get('uuid')) {
      lastFactsSection?.get('name')?.setValue(updatedSection.name);
      if (!lastFactsSection?.get('uuid')) {
        console.warn(
          `'uuid' control is missing in last messagingTheme group. Adding it now.`,
        );
        lastFactsSection.addControl(
          'uuid',
          new FormControl(updatedSection.uuid),
        );
      } else {
        lastFactsSection?.get('uuid')?.setValue(updatedSection.uuid);
      }

      newFactSectionsFacts = lastFactsSection?.get(
        SimulationConstants.FACTS_SECTION,
      ) as FormArray;
      newFactSectionsFacts.clear();

      updatedSection.facts.forEach((fact) => {
        newFactSectionsFacts.push(
          this.fb.group({
            uuid: [fact.uuid],
            rawText: [fact.rawText],
            displayTextCorrect: [fact.displayTextCorrect],
            displayTextIncorrect: [fact.displayTextIncorrect],
            threshold: [fact.threshold],
          }),
        );
      });

      newFactSection = lastFactsSection;
    } else {
      newFactSectionsFacts = this.fb.array(
        updatedSection.facts.map((fact) =>
          this.fb.group({
            uuid: [fact.uuid],
            rawText: [fact.rawText],
            displayTextCorrect: [fact.displayTextCorrect],
            displayTextIncorrect: [fact.displayTextIncorrect],
            threshold: [fact.threshold],
          }),
        ),
      );

      newFactSection = this.fb.group({
        name: [updatedSection.name],
        uuid: [updatedSection.uuid],
        facts: newFactSectionsFacts,
      });

      this.messagingFactSections.push(newFactSection);
      this.syncThemesItems();
    }

    if (this.messagingFactSections.length === 1) {
      this.updateMessageAdherencePromptTypeInForm(
        SimulationConstants.MESSAGING_PROMPT_TYPE_V2,
      );
    }
  }

  addThemeComponent(
    updatedSection: ISection,
    factsSection?: FormGroup,
    factsSectionFacts?: FormArray<FormGroup>,
  ) {
    const componentRef = this.container.createComponent(
      MessagingThemeComponent,
    );

    // Store the component reference by section UUID or use a fallback
    const sectionUuid = updatedSection.uuid;
    const key = sectionUuid || `section_${this.messagingFactSections.length}`;
    this.componentRefs.set(key, componentRef);

    componentRef.instance.sectionUuid = updatedSection.uuid;
    componentRef.instance.sectionName = updatedSection.name;

    if (factsSection && factsSectionFacts) {
      componentRef.instance.messagingFactSection = factsSection;
      componentRef.instance.messagingFactSectionFacts = factsSectionFacts;
      componentRef.instance.messagingFactSections = this.messagingFactSections;
      componentRef.instance.loadFormFromProps?.();
    }

    componentRef.instance.deleteRequested.subscribe((uuid: string) => {
      this.handleDeleteTheme(uuid);
    });

    if (this.shouldStartEditing) {
      this.shouldStartEditing = false;
      setTimeout(() => componentRef.instance.startEditing());
    }
  }

  handleDeleteTheme(sectionUuid: string): void {
    this.authorService.deleteSection(this.callUuid, sectionUuid).subscribe({
      next: () => {
        const index = this.messagingFactSections.controls.findIndex(
          (section) => section?.get('uuid')?.value === sectionUuid,
        );

        if (index !== -1) {
          this.messagingFactSections.removeAt(index);
          this.syncThemesItems();

          if (this.messagingFactSections.length === 0) {
            this.updateMessageAdherencePromptTypeInForm(
              SimulationConstants.MESSAGING_PROMPT_TYPE,
            );
          }
        }
      },
      error: (err) => {
        console.error('Failed to delete section:', err);
      },
    });
  }

  onThemesReordered(items: DraggableItem[]): void {
    const reordered = items as unknown as FormGroup[];

    // Rebuild themes FormArray in new order without emitting
    const controls = reordered.slice();
    while (this.messagingFactSections.length > 0) {
      this.messagingFactSections.removeAt(0, { emitEvent: false });
    }
    controls.forEach((ctrl) =>
      this.messagingFactSections.push(ctrl, { emitEvent: false }),
    );
    this.syncThemesItems();

    // TODO: call a dedicated reorder endpoint when available from the backend.
    // Currently updateSectionWithFacts does not guarantee section order persistence.
    console.log(
      'Themes reordered locally. Backend reorder endpoint needed to persist.',
    );
  }

  getOtherThemeListIds(currentItem: DraggableItem): string[] {
    const currentUuid = (currentItem as unknown as FormGroup).get(
      'uuid',
    )?.value;
    return this.messagingFactSections.controls
      .map((ctrl) => (ctrl as FormGroup).get('uuid')?.value)
      .filter((uuid) => uuid && uuid !== currentUuid)
      .map((uuid) => 'facts-' + uuid);
  }

  private syncThemesItems(): void {
    this.themesItems =
      (this.messagingFactSections?.controls.slice() as unknown as DraggableItem[]) ??
      [];
  }

  asFormGroup(item: DraggableItem): FormGroup {
    return item as unknown as FormGroup;
  }
}
